import { AppMenus } from '@/abpPro/AppMenus';
import { abpService, appAuthService, appSessionService } from '@/shared/abp';
import loc from '@/shared/i18n/localization.service';
import NProgress from 'nprogress';
import { Route, VueRouter } from 'vue-router/types/router';

import reuseTabService from '../common/reuse-tab/reuse-tab.service';

const defaultLoginUrl = '/account/login';
const defaultSetPasswordUrl = '/account/setPassword';
const defaultMainUrl = '/app';

/**
 * 路由守卫
 */
class AppRouteGuard {
    private inited = false;
    private _loginUrl = defaultLoginUrl;
    private _mainUrl = defaultMainUrl;
    private _whiteList = [defaultLoginUrl, defaultSetPasswordUrl];

    get whiteList(): string[] {
        return this._whiteList;
    }

    set whiteList(val: string[]) {
        this._whiteList = Array.isArray(val) ? val : [];
    }

    get loginUrl(): string {
        return this._loginUrl;
    }

    set loginUrl(val: string) {
        this._loginUrl = val || defaultLoginUrl;
    }

    get mainUrl(): string {
        return this._mainUrl;
    }

    set mainUrl(val: string) {
        this._mainUrl = val || defaultMainUrl;
    }

    public init(rootRouting: VueRouter) {
        if (this.inited) {
            return;
        }
        this.inited = true;
        NProgress.configure({ showSpinner: false });

        rootRouting.beforeResolve((to: Route, from: Route, next: any) => this.beforeResolve(to, from, next));
        rootRouting.beforeEach((to: Route, from: Route, next: any) => this.beforeEach(to, from, next));
        rootRouting.afterEach((to: Route, from: Route) => this.afterEach(to, from));
    }

    public beforeResolve(to: Route, from: Route, next: any) {
        if (to.path === '/') {
            next({ path: this.mainUrl });
            return;
        }

        if (to.path === defaultSetPasswordUrl) {
            next();
            return;
        }

        this.checkErrpath(to);
        this.setModuleId(to);
        NProgress.start();

        const inWhiteList = this.whiteList.some((o) => o === to.path || o.startsWith(to.path));
        if (inWhiteList) {
            next();
            return;
        }

        if (!this.canActivate(to, from, next)) {
            return;
        }

        this.setTitle(to);
        next();
    }

    private setTitle(to: Route) {
        const titleKey = (to.meta && (to.meta as any).title) || '';
        if (abpService.abp.userInfo.tenantName) {
            document.title = `${abpService.abp.userInfo.tenantName}-${loc.l(titleKey)}-${loc.l('LPA')}`;
            return;
        }
        document.title = `${loc.l('LPA')}-${titleKey}`;
    }

    public setModuleId(to: Route) {
        const moduleId = (to.query && (to.query.moduleId as string)) || to.path;
        const parentItem: any = reuseTabService.getMaxParantMenus(moduleId);
        if (!parentItem || !parentItem.children) {
            return;
        }

        reuseTabService.getChildren(parentItem.children, to.path);
        if (reuseTabService.tabKey) {
            abpService.setModuleKey(reuseTabService.tabKey);
        }
    }

    public checkErrpath(to: Route) {
        const side = abpService.abp.session.multiTenancySide.toString();
        if (side === 'Tenant' && abpService.abp.session.userId) {
            const invalidHostMenus = AppMenus.HostMenus.filter(
                (item) => item.link === to.path && item.i18n !== 'Dashboard' && item.public !== true
            );

            if (invalidHostMenus.length > 0) {
                appAuthService.logout();
            }
        }
    }

    public beforeEach(to: Route, from: Route, next: any) {
        next();
    }

    public afterEach(to: Route, from: Route) {
        NProgress.done();
    }

    private canActivate(to: Route, from: Route, next: any): boolean {
        if (!appSessionService.user) {
            next({ path: this.loginUrl, query: { redirect: to.fullPath } });
            return false;
        }
        return true;
    }

    private selectBestRoute(): string {
        if (!appSessionService.user) {
            return this.loginUrl;
        }
        return this.mainUrl;
    }
}

const appRouteGuard = new AppRouteGuard();
export default appRouteGuard;
